import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { UserRole } from '../hooks/useAuth';
import { useAuth } from '../hooks/useAuth';
import { useRole } from '../hooks/useRole';

/**
 * Role-gated render helpers.
 *
 * Cohesion rule 5: every visibility-by-role check uses ONE of these
 * components (or the useRole hook for inline logic). No ad-hoc
 * role-string comparisons anywhere else.
 *
 * Usage:
 *
 *   <RequireMinRole role="MANAGER">
 *     <QuickStatsButton />
 *   </RequireMinRole>
 *
 *   <RequireRole exact roles={['CASHIER', 'SUPERVISOR']}>
 *     <TillOnlyShortcut />
 *   </RequireRole>
 *
 * If the user fails the role check, NOTHING renders (no greyed-out
 * disabled state) — by design. Cashiers should never see admin
 * chrome at all. Pass `fallback={...}` for the rare case you want
 * a placeholder.
 */

interface RequireMinRoleProps {
  role: UserRole;
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequireMinRole({ role, children, fallback = null }: RequireMinRoleProps) {
  const { hasMinRole } = useRole();
  return <>{hasMinRole(role) ? children : fallback}</>;
}

interface RequireRoleProps {
  roles: UserRole[];
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequireRole({ roles, children, fallback = null }: RequireRoleProps) {
  const { hasExactRole } = useRole();
  return <>{hasExactRole(...roles) ? children : fallback}</>;
}

/**
 * RequireMinRoleRoute — full route guard for an admin-tier route.
 *
 * Behaviour:
 *  - Not signed in → redirect to /login (remembers the requested path)
 *  - Signed in but role < minimum → redirect to /pos (cashier home)
 *  - While loading → render nothing (avoids flash)
 *
 * Use this on Routes; use <RequireMinRole> inline for visibility.
 */
interface RequireMinRoleRouteProps {
  role: UserRole;
  children: ReactNode;
}

export function RequireMinRoleRoute({ role, children }: RequireMinRoleRouteProps) {
  const { isAuthenticated, loading } = useAuth();
  const { hasMinRole } = useRole();
  const location = useLocation();

  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!hasMinRole(role)) {
    return <Navigate to="/pos" replace />;
  }
  return <>{children}</>;
}
